import {
    captureError,
    generateName,
    getCurrentGame,
    getRandomChoice,
    getRandomInt,
    getTalentCompany,
    myround
} from "./helpers.js";
import {Companies, db, YearlyRankings} from "../models/models.js";
import {getGameActiveCompanies, getGameCurrentUserCompany} from "./games.js";
import {getRosterActiveTagTeams} from "./rosters.js";
import {globalUser} from "./globals.js";
import {getCompanyRosterByType} from "./companies.js";
import {getRosterBrands, getRosterSuperstars, getRosterTagTeams} from "../models/roster.js";
import {addCompanyMessages, getCompanyChampionships, getCompanyRosters} from "../models/company.js";
import {getRosterMemberSuperstars, updateSuperstarPopularity} from "./superstars.js";
import {getChampionshipDisplayName} from "../ui/ui-helpers.js";
import {checkAreChampionshipsEqual, getUniqueChampionships} from "./championships.js";
import {createTopSuperstarPointsMessage, createTopSuperstarPopularityMessage} from "./messages.js";
import {YearlyRanking} from "../models/yearlyRanking.js";
import {addGameYearlyRankings, getGameYearlyRankings} from "../models/game.js";

async function getViewRankingsData(companyUid, period) {  // get_roster_rankings_view
    let data = {};
    const currentGame = await getCurrentGame();

    if (!globalUser.paid) {
        data["error"] = "not-paid";
        return data;
    }

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get talent and championships
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const talent = (await getRosterSuperstars(mainRoster)).concat(await getRosterActiveTagTeams(mainRoster));
    const championships = await getCompanyChampionships(selectedCompany);

    // Get ranking data
    data["rankingData"] = await getWinLossRankingData(talent, championships, period);

    // Get brand names
    const rosterBrands = await getRosterBrands(mainRoster);
    data["brandNames"] = rosterBrands.map(x => x.name);

    return data;
}

async function getWinLossRankingData(talent, championships, period) {  // get_win_loss_ranking_data
    let rankingData = [];

    // Get data for each talent
    for (const talentMember of talent) {
        let d = {};
        let memberSuperstars = await getRosterMemberSuperstars(talentMember);

        // Get name, pronoun, type
        d["name"] = talentMember.name;
        d["pronoun"] = (memberSuperstars.every(x => x.pronoun === memberSuperstars[0].pronoun)) ?
            memberSuperstars[0].pronoun : null;
        d["brandName"] = (memberSuperstars.every(x => x.brandName === memberSuperstars[0].brandName)) ?
            memberSuperstars[0].brandName : null;
        d["type"] = talentMember.objectName;

        // Get wins/losses based on period
        if (period === 'current') {
            d["wins"] = talentMember.winsTemporary;
            d["losses"] = talentMember.lossesTemporary;
        }
        else if (period === 'allTime') {
            d["wins"] = talentMember.wins;
            d["losses"] = talentMember.losses;
        }

        // Get championships
        let talentChampionships = [];
        if (talentMember.objectName === 'superstar') {
            talentChampionships = championships.filter(x => x.superstarId === talentMember.$loki);
            d["championships"] = talentChampionships.map(x => getChampionshipDisplayName(x, true, false, false));
        }
        else {
            const uniqueTagTitles = getUniqueChampionships(championships.filter(x => x.tagTeam || x.trio));
            for (const c of uniqueTagTitles) {
                // Check if each title of the set is held by this team's members
                let tagChampionshipsSet = championships.filter(y => checkAreChampionshipsEqual(y, c));
                const memberSuperstarsIds = memberSuperstars.map(x => x.$loki);
                if (tagChampionshipsSet.every(x => memberSuperstarsIds.indexOf(x.superstarId) >= 0)) {
                    talentChampionships.push(c);
                }
            }
            d["championships"] = talentChampionships.map(x => getChampionshipDisplayName(x, true));
        }

        rankingData.push(d);
    }

    rankingData.sort(sortWinLossRanking);

    return rankingData;
}

function sortWinLossRanking(a, b) {  // sort_win_loss_ranking_function
    return (
        (b["wins"] + b["losses"] > 0) - (a["wins"] + a["losses"] > 0) ||
        (b["wins"] > 0) - (a["wins"] > 0) ||
        (b["wins"] - b["losses"]) - (a["wins"] - a["losses"]) ||
        (b["wins"]) - (a["wins"])
    )
}

async function processYearlyRanking(game, mainCompany) {  // process_yearly_ranking
    let data = {};
    let {superstars, championships, tagTeams, indieRosters} = await getRankingObjects(game);
    let updated = false;

    try {
        // Don't get yearly rankings if story or free user
        if (!mainCompany.storyName && globalUser.paid) {

            // Load ranking if present
            let yearlyRanking = await loadYearlyRanking(game, mainCompany.realYear - 1);

            // Get yearly ranking if not present and points added
            let pointsPresent = !superstars.every(x => x.kayfabePointsTemporary === 0);
            if (!yearlyRanking && pointsPresent) {

                // Get ranking for current year
                let rankingData = await getRankingDataForYear(superstars, championships, indieRosters);

                // Store ranking
                yearlyRanking = await storeYearlyRanking(rankingData, game, mainCompany);

                // Add boost to top superstar
                let topSuperstar = superstars.find(x => x.name === rankingData[0].name);
                if (topSuperstar) {
                    let popularityBoost = addTopSuperstarPopularityBoost(topSuperstar);

                    // Add message if superstar belongs to user's company
                    let company = await getTalentCompany(topSuperstar);
                    if (company && company.userControlled) {
                        let newMessage = await createTopSuperstarPopularityMessage(topSuperstar, popularityBoost,
                            company, game.monthCount, game.year);
                        await addCompanyMessages(company, [newMessage]);

                        // Add creative points boost and message
                        let creativePointsBoost = addTopSuperstarCreativePointsBoost(company);
                        newMessage = await createTopSuperstarPointsMessage(topSuperstar, creativePointsBoost,
                            company, game.monthCount, game.year);
                        await addCompanyMessages(company, [newMessage]);
                    }
                }

                updated = true;
            }

            if (yearlyRanking) {
                data = getYearlyRankingData(yearlyRanking, game);
            }
        }
    } catch (error) {
        captureError(error)
    }

    // Reset temporary attributes
    if (!game.yearlyAttributesResetRan) {
        resetTalentTemporaryAttributes([...superstars, ...tagTeams]);
        game.yearlyAttributesResetRan = true;
        updated = true;
    }

    // Update database if changes made
    if (updated) {
        db.saveDatabase()
    }

    return data;
}

function getYearlyRankingData(yearlyRanking, game) {  // Previously get_yearly_ranking_data
    let data = {};
    const gameRandom = new Math.seedrandom(game.meta.created);
    data['rankingData'] = yearlyRanking.data;
    data['year'] = yearlyRanking.year;
    data['editor'] = generateName(getRandomChoice(['male', 'female'], gameRandom()), gameRandom());
    return data;
}

function getSuperstarRankingHighlight(superstar, championships) {  // get_superstar_ranking_highlight
    let highlights = [];

    let superstarChampionships = championships.filter(x => x.superstarId === superstar.$loki);
    if (superstarChampionships.length) {
        highlights.push(...superstarChampionships.map(x => getChampionshipDisplayName(x, true, false, false)));
    }

    let totalMatches = superstar.winsTemporary + superstar.lossesTemporary;
    let winPercentage = totalMatches > 0 ? Math.round(superstar.winsTemporary / totalMatches * 100) : null;
    if (winPercentage >= 80) {
        highlights.push(`${winPercentage}% Match Wins`);
    }

    return highlights.length ? highlights[0] : null;
}

async function storeYearlyRanking(rankingData, game, mainCompany) {  // store_yearly_ranking
    let yearlyRanking = YearlyRanking();
    YearlyRankings.insert(yearlyRanking);
    yearlyRanking.data = rankingData;
    yearlyRanking.year = mainCompany.realYear - 1;
    await addGameYearlyRankings(game, [yearlyRanking]);
    return yearlyRanking;
}

async function loadYearlyRanking(game, year) {
    return (await getGameYearlyRankings(game)).find(x => x.year === year);
}

async function getRankingDataForYear() {  // get_ranking_data_for_year
    return
}

function setupGetRankingDataForYear(newFunction) {
    getRankingDataForYear = newFunction
}

function resetTalentTemporaryAttributes(talentArray) {  // reset_talent_temporary_attributes
    // Reset temporary wins, losses and kayfabe points
    for (let talent of talentArray) {
        if (talent.objectName === 'superstar') {
            talent.kayfabePointsTemporary = 0;
        }
        talent.winsTemporary = 0;
        talent.lossesTemporary = 0;
    }
    return true;
}

async function getRankingObjects(game) {  // get_ranking_objects
    // Get superstars and championships
    let companies = await getGameActiveCompanies(game);
    let superstars = [];
    let championships = [];
    let tagTeams = [];
    let indieRosters = [];

    for (let company of companies) {
        championships.push(...await getCompanyChampionships(company));

        // Get superstars and teams
        let rosters = (await getCompanyRosters(company))
            .filter(x => ['main', 'training', 'trainingAvailable', 'indie', 'indieAvailable'].includes(x.type));
        for (const roster of rosters) {
            superstars.push(...await getRosterSuperstars(roster));
            tagTeams.push(...await getRosterTagTeams(roster));
            if (['indie', 'indieAvailable'].includes(roster.type)) {
                indieRosters.push(roster)
            }
        }
    }
    return {superstars, championships, tagTeams, indieRosters};
}

function addTopSuperstarPopularityBoost(superstar) {  // add_top_superstar_popularity_boost
    let boost = getRandomInt(40, 60);
    boost = updateSuperstarPopularity(superstar, boost);
    return boost;
}

function addTopSuperstarCreativePointsBoost(company) {  // add_top_superstar_creative_points_boost
    let boost = myround(getRandomInt(200, 300), 10);
    company.points += boost;
    return boost;
}

export {getViewRankingsData, processYearlyRanking, getYearlyRankingData, getSuperstarRankingHighlight,
    setupGetRankingDataForYear}